import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Settings, Save, KeyRound, Plus, Trash2, Copy, Check, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { COPY_API_MODELS as MODEL_OPTIONS } from "@shared/copy-api-models";

interface ApiKeyListItem {
  id: string;
  keyPrefix: string;
  name: string;
  createdBy: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string | null;
}

function keyStatus(k: ApiKeyListItem): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  if (k.revokedAt) return { label: "Revoked", variant: "destructive" };
  if (k.expiresAt && new Date(k.expiresAt).getTime() <= Date.now()) return { label: "Expired", variant: "secondary" };
  return { label: "Active", variant: "default" };
}

export default function CopyApiManager() {
  const { toast } = useToast();

  // -------------------------------------------------------------------------
  // Settings panel
  // -------------------------------------------------------------------------
  const [settings, setSettings] = useState<any>(null);

  const { data: copyApiSettings, isLoading: settingsLoading } = useQuery<any>({
    queryKey: ["/api/copy-api-settings"],
  });

  useEffect(() => {
    if (copyApiSettings) setSettings(copyApiSettings);
  }, [copyApiSettings]);

  const saveSettings = useMutation({
    mutationFn: async (data: any) => apiRequest("/api/copy-api-settings", "PUT", data),
    onSuccess: () => {
      toast({ title: "Settings saved", description: "Copy API settings have been updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/copy-api-settings"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to save settings", variant: "destructive" });
    },
  });

  const handleSaveSettings = () => {
    if (!settings) return;
    const { id, createdAt, updatedAt, ...toSave } = settings;
    saveSettings.mutate(toSave);
  };

  // -------------------------------------------------------------------------
  // API key management
  // -------------------------------------------------------------------------
  const { data: apiKeys = [], isLoading: keysLoading } = useQuery<ApiKeyListItem[]>({
    queryKey: ["/api/api-keys"],
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyExpiresInDays, setNewKeyExpiresInDays] = useState("");
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyListItem | null>(null);

  const createKey = useMutation({
    mutationFn: async () => {
      const body: { name: string; expiresInDays?: number } = { name: newKeyName.trim() };
      const days = parseInt(newKeyExpiresInDays, 10);
      if (!Number.isNaN(days) && days > 0) body.expiresInDays = days;
      return apiRequest("/api/api-keys", "POST", body);
    },
    onSuccess: (data: any) => {
      setCreateOpen(false);
      setNewKeyName("");
      setNewKeyExpiresInDays("");
      setRevealedKey(data.key); // shown exactly once
      setCopied(false);
      queryClient.invalidateQueries({ queryKey: ["/api/api-keys"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to create key", variant: "destructive" });
    },
  });

  const revokeKey = useMutation({
    mutationFn: async (id: string) => apiRequest(`/api/api-keys/${id}`, "DELETE"),
    onSuccess: () => {
      toast({ title: "Key revoked", description: "The API key can no longer be used." });
      setRevokeTarget(null);
      queryClient.invalidateQueries({ queryKey: ["/api/api-keys"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to revoke key", variant: "destructive" });
    },
  });

  const copyRevealedKey = async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Select and copy the key manually.", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Copy API Configuration
          </CardTitle>
          <CardDescription>
            One generic prompt and model used by all machine clients of the public copy-generation API.
            This is separate from the per-station Copy Engine.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settingsLoading || !settings ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="copyApiPrompt">System Prompt</Label>
                <Textarea
                  id="copyApiPrompt"
                  value={settings.systemPrompt ?? ""}
                  onChange={(e) => setSettings({ ...settings, systemPrompt: e.target.value })}
                  className="min-h-[220px] font-mono text-sm"
                  placeholder="Enter the generic sponsorship/underwriting copy prompt..."
                  data-testid="input-copy-api-prompt"
                />
                <p className="text-xs text-gray-500">
                  The model always returns three copy variants as JSON. Reference any incoming variable inline as a
                  {' '}<code>{'{placeholder}'}</code>; anything you do not reference is appended automatically as
                  labeled reference data, so nothing is lost.
                </p>
                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                  <p><strong>Available variables:</strong></p>
                  <p>• {'{sponsorName}'} - sponsor name (required)</p>
                  <p>• {'{copyType}'} - "broadcast" or "event"</p>
                  <p>• {'{preamble}'} - station preamble / boilerplate</p>
                  <p>• {'{additionalDetails}'} - extra details supplied by the caller</p>
                  <p>• {'{pronunciationNotes}'} - how to pronounce names</p>
                  <p>• {'{campaignGoals}'} - goals of the campaign</p>
                  <p>• {'{previousVariants}'} - prior options to avoid on regenerate</p>
                  <p>• {'{websiteContent}'} - text from the fetched context website</p>
                  <p className="pt-1"><strong>Broadcast:</strong></p>
                  <p>• {'{website}'} - sponsor website URL</p>
                  <p>• {'{moreInfoLink}'} - more-info link (reference only, not fetched)</p>
                  <p className="pt-1"><strong>Event:</strong></p>
                  <p>• {'{eventName}'} - event name</p>
                  <p>• {'{eventWebsite}'} - event website URL (fetched for context)</p>
                  <p>• {'{eventDatesAndTimes}'} - event dates and times</p>
                  <p>• {'{eventLocation}'} - event location</p>
                  <p>• {'{ticketsLink}'} - tickets / info link (reference only, not fetched)</p>
                </div>
              </div>

              <div className="space-y-2 md:max-w-md">
                <Label htmlFor="copyApiModel">Model</Label>
                <Select
                  value={settings.model}
                  onValueChange={(v) => setSettings({ ...settings, model: v })}
                >
                  <SelectTrigger id="copyApiModel" data-testid="select-copy-api-model">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_OPTIONS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Generation settings (temperature, output length) are tuned per model and managed automatically.
                  OpenAI models use our website scraper; Claude and Gemini fetch the sponsor URL themselves.
                </p>
              </div>

              <div className="space-y-2 pt-2">
                <Label className="text-sm font-semibold">Cost guardrails</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="perKeyHourly" className="text-xs text-gray-600">Per-key hourly limit</Label>
                    <Input
                      id="perKeyHourly"
                      type="number"
                      min="1"
                      value={settings.perKeyHourlyLimit ?? 60}
                      onChange={(e) => setSettings({ ...settings, perKeyHourlyLimit: parseInt(e.target.value, 10) })}
                      data-testid="input-per-key-hourly"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="perKeyDaily" className="text-xs text-gray-600">Per-key daily cap</Label>
                    <Input
                      id="perKeyDaily"
                      type="number"
                      min="1"
                      value={settings.perKeyDailyLimit ?? 300}
                      onChange={(e) => setSettings({ ...settings, perKeyDailyLimit: parseInt(e.target.value, 10) })}
                      data-testid="input-per-key-daily"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="globalDaily" className="text-xs text-gray-600">Global daily ceiling</Label>
                    <Input
                      id="globalDaily"
                      type="number"
                      min="1"
                      value={settings.globalDailyLimit ?? 2000}
                      onChange={(e) => setSettings({ ...settings, globalDailyLimit: parseInt(e.target.value, 10) })}
                      data-testid="input-global-daily"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Hourly limits are an in-memory burst guard. Daily caps and the global ceiling are persisted and
                  enforced across restarts. Hitting the global ceiling returns 503 until the next day (UTC).
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveSettings}
                  disabled={saveSettings.isPending}
                  data-testid="button-save-copy-api-settings"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saveSettings.isPending ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* API keys */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5" />
                API Keys
              </CardTitle>
              <CardDescription>
                Keys authenticate first-party machine clients (HubSpot plugin, self-serve tool) via the
                X-API-Key header. The raw key is shown once at creation and stored only as a hash.
              </CardDescription>
            </div>
            <Dialog
              open={createOpen}
              onOpenChange={(open) => {
                setCreateOpen(open);
                if (!open) {
                  setNewKeyName("");
                  setNewKeyExpiresInDays("");
                }
              }}
            >
              <DialogTrigger asChild>
                <Button data-testid="button-create-api-key">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Key
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create API Key</DialogTitle>
                  <DialogDescription>
                    Give the key a recognizable name. The raw key will be shown only once.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newKeyName">Name</Label>
                    <Input
                      id="newKeyName"
                      placeholder="e.g., HubSpot plugin (production)"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      data-testid="input-new-key-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newKeyExpiry">Expires in days (optional)</Label>
                    <Input
                      id="newKeyExpiry"
                      type="number"
                      min="1"
                      placeholder="Leave blank for no expiry"
                      value={newKeyExpiresInDays}
                      onChange={(e) => setNewKeyExpiresInDays(e.target.value)}
                      data-testid="input-new-key-expiry"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="button-cancel-create-key">
                    Cancel
                  </Button>
                  <Button
                    onClick={() => createKey.mutate()}
                    disabled={createKey.isPending || !newKeyName.trim()}
                    data-testid="button-confirm-create-key"
                  >
                    {createKey.isPending ? "Creating..." : "Create Key"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {keysLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <KeyRound className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No API keys yet</p>
              <p className="text-sm text-gray-400">Create a key to let a machine client call the copy API</p>
            </div>
          ) : (
            <div className="space-y-2">
              {apiKeys.map((k) => {
                const status = keyStatus(k);
                const isActive = status.label === "Active";
                return (
                  <div
                    key={k.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                    data-testid={`api-key-item-${k.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <KeyRound className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{k.name}</p>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </div>
                        <p className="text-sm text-gray-500 font-mono">{k.keyPrefix}…</p>
                        <p className="text-xs text-gray-400">
                          {k.lastUsedAt
                            ? `Last used ${formatDistanceToNow(new Date(k.lastUsedAt), { addSuffix: true })}`
                            : "Never used"}
                          {k.expiresAt ? ` • Expires ${formatDistanceToNow(new Date(k.expiresAt), { addSuffix: true })}` : ""}
                        </p>
                      </div>
                    </div>
                    {isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevokeTarget(k)}
                        data-testid={`button-revoke-key-${k.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* One-time key reveal */}
      <Dialog open={!!revealedKey} onOpenChange={(open) => { if (!open) setRevealedKey(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Copy your API key now
            </DialogTitle>
            <DialogDescription>
              This is the only time the full key will be shown. Store it somewhere safe. If you lose it, revoke it
              and create a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={revealedKey ?? ""} className="font-mono text-sm" data-testid="text-revealed-key" />
            <Button variant="outline" size="icon" onClick={copyRevealedKey} data-testid="button-copy-key">
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealedKey(null)} data-testid="button-done-reveal">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => { if (!open) setRevokeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget ? `"${revokeTarget.name}" (${revokeTarget.keyPrefix}…)` : ""} will stop working
              immediately. Any client using it will receive a 401. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-revoke">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revokeTarget && revokeKey.mutate(revokeTarget.id)}
              data-testid="button-confirm-revoke"
            >
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
